// React
import React, { useCallback, useEffect, useRef, useState } from 'react'

// Material UI
import { TextField, CircularProgress, MenuItem, Box } from '@mui/material'

// Types
import {
    EntityListWithPagination,
    EntityListWithPaginationFields,
    EntityPagination,
    EntityPaginationFields,
    EntityWithIdAndDescription,
    EntityWithIdAndDescriptionFields
} from 'src/types'
import { Control, Controller } from 'react-hook-form'

type SelectProps<TOption extends EntityWithIdAndDescription> /*extends TextFieldProps*/ = {
    loadOptions?:
    | TOption[]
    | (() => TOption[])
    | (() => Promise<TOption[]>)
    | ((page: number) => Promise<EntityListWithPagination<TOption>>)
    control?: Control<any>  // TODO: ver de extender bien el tipo este
    name?: string
    label?: string
}

const Select = <TOption extends EntityWithIdAndDescription>(props: SelectProps<TOption>) => {
    const { loadOptions, control, name, label } = props

    const [options, setOptions] = React.useState<TOption[]>()
    const [paginacion, setPaginacion] = useState<EntityPagination>()

    // Status of the options loading process.
    const [loadingOptionsStatus, setLoadingOptionsStatus] = useState<'idle' | 'loading' | 'error' | 'success'>('idle')

    const load = (page: number) => {
        if (!loadOptions) return

        if (Array.isArray(loadOptions)) {
            setOptions(loadOptions)
            setLoadingOptionsStatus('success')

            return
        }

        const result = loadOptions(page)

        if (Array.isArray(result)) {
            setOptions(result)
            setLoadingOptionsStatus('success')

            return
        }

        setLoadingOptionsStatus('loading')

        result
            .then(res => {
                if (Array.isArray(res)) {
                    setOptions(res)
                } else {
                    setOptions(prev => [...(prev ? prev : []), ...res[EntityListWithPaginationFields.List]])
                    setPaginacion(res[EntityListWithPaginationFields.Pagination])
                }
                setLoadingOptionsStatus('success')
            })
            .catch(err => {
                setLoadingOptionsStatus('error')
            })
    }

    useEffect(() => load(1), [])

    const observer = useRef<IntersectionObserver>()

    const hasMore =
        paginacion && paginacion[EntityPaginationFields.ActualPage] !== paginacion[EntityPaginationFields.CantPages]

    const lastElementRef = useCallback(
        (node: HTMLLIElement) => {
            if (loadingOptionsStatus === 'loading') return
            if (observer.current) observer.current.disconnect()
            observer.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting && hasMore) {
                    load(paginacion[EntityPaginationFields.ActualPage] + 1)
                }
            })
            if (node) observer.current.observe(node)
        },
        [loadingOptionsStatus, hasMore]
    )

    return (
        <Controller
            control={control}
            name={name || ''}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                    sx={{ m: 0 }}
                    size='small'
                    label={label}
                    value={value}
                    error={!!error}
                    helperText={error && error.message}
                    onChange={onChange}
                    select
                    fullWidth
                >
                    {!options
                        ? loadingOptionsStatus === 'loading' && <CircularProgress size={20} />
                        : options.map((option, index) => {
                            if (options.length === index + 1) {
                                return (
                                    <MenuItem
                                        ref={lastElementRef}
                                        key={option[EntityWithIdAndDescriptionFields.Id]}
                                        value={option[EntityWithIdAndDescriptionFields.Id]}
                                    >
                                        {option[EntityWithIdAndDescriptionFields.Description]}
                                    </MenuItem>
                                )
                            } else {
                                return (
                                    <MenuItem
                                        key={option[EntityWithIdAndDescriptionFields.Id]}
                                        value={option[EntityWithIdAndDescriptionFields.Id]}
                                    >
                                        {option[EntityWithIdAndDescriptionFields.Description]}
                                    </MenuItem>
                                )
                            }
                        })}
                    {loadingOptionsStatus === 'loading' && options?.length && (
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={20} />
                        </Box>
                    )}
                </TextField>
            )}
        />
    )
}

// TODO: ListItemText con descripcion y si hay, un caption/subtitulo en secondary

export default Select
